import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { Mail, MapPin, MessageCircle, ArrowUpRight } from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";
import { DICTS } from "@/lib/i18n";
import { buildContactEmailDraft } from "@/lib/contact-email";
import storyImage from "@/assets/story-image.jpg";
import { useHeroVideoUrls } from "@/hooks/useHeroVideoUrls";
import { PARTNER_PORTAL_URL } from "@/lib/partner-portal";
import { LECMO_WA_NUMBER } from "@/lib/agentConfig";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import { softCtaClass } from "@/components/ui/soft-cta";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/contact")({
  headers: publicCacheHeaders,
  head: () => ({
    meta: [
      { title: DICTS.en["contact.meta.title"] },
      { name: "description", content: DICTS.en["contact.meta.description"] },
      { property: "og:title", content: DICTS.en["contact.meta.title"] },
      { property: "og:description", content: DICTS.en["contact.meta.description"] },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/contact` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/contact` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          url: `${PUBLIC_SITE_ORIGIN}/contact`,
          name: DICTS.en["contact.meta.title"],
          description: DICTS.en["contact.meta.description"],
          mainEntity: {
            "@type": "Organization",
            name: "Lecmo Parfums",
            url: `${PUBLIC_SITE_ORIGIN}/`,
            contactPoint: [
              {
                "@type": "ContactPoint",
                contactType: "customer support",
                email: "info@lecmo.org",
                availableLanguage: ["en", "ar"],
                areaServed: ["AE", "SA", "OM", "IQ"],
              },
              {
                "@type": "ContactPoint",
                contactType: "sales",
                url: `https://wa.me/${LECMO_WA_NUMBER}`,
                availableLanguage: ["en", "ar"],
              },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Lecmo", item: `${PUBLIC_SITE_ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: "Contact", item: `${PUBLIC_SITE_ORIGIN}/contact` },
          ],
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const heroVideo = useHeroVideoUrls("contact");
  const { t, locale } = useLocale();
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    window.location.href = buildContactEmailDraft({ name, email, message }, locale);
  };

  return (
    <SiteShell>
      <PageHero
        pageKey="contact"
        title={t("contact.title")}
        subtitle={t("contact.subtitle")}
        videoUrl={heroVideo.desktop}
        mobileVideoUrl={heroVideo.mobile}
        campaignId={heroVideo.campaignId}
        image={heroVideo.poster || storyImage}
        alt={t("contact.title")}
      />

      <section className="mx-auto w-full max-w-6xl px-4 pt-10 sm:px-6 md:pt-14">
        <a
          href={PARTNER_PORTAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex flex-col gap-4 border-t border-b border-border px-2 py-8 transition-colors hover:bg-secondary/40 md:flex-row md:items-center md:justify-between md:px-6 md:py-10"
        >
          <div className="space-y-2">
            <p className="t-eyebrow tracking-[var(--tracking-wider)] text-muted-foreground">{t("footer.partners")}</p>
            <h2 className="font-serif text-2xl leading-tight tracking-[-0.01em] md:text-3xl">{t("contact.partners.title")}</h2>
            <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">{t("contact.partners.body")}</p>
          </div>
          <span className={`${softCtaClass} shrink-0 self-start md:self-auto`}>
            {t("contact.partners.cta")}
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </a>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 md:py-20">
        <div className="grid gap-16 md:grid-cols-[1fr_1.4fr]">
          <div className="space-y-8">
            <Item icon={<MessageCircle className="h-4 w-4" />} title={t("contact.whatsapp_title")}>
              <a
                href="https://wa.me/971508877701"
                className="text-foreground hover:text-primary"
                target="_blank"
                rel="noreferrer"
              >
                <span lang="en" dir="ltr" style={{ direction: "ltr" }}>
                  +971 50 887 7701
                </span>
              </a>
            </Item>
            <Item icon={<Mail className="h-4 w-4" />} title={t("contact.email_title")}>
              <a href="mailto:info@lecmo.org" className="text-foreground hover:text-primary">
                info@lecmo.org
              </a>
            </Item>
            <Item icon={<MapPin className="h-4 w-4" />} title={t("contact.atelier_title")}>
              {t("contact.atelier_line1")}
              <br />
              {t("contact.atelier_line2")}
            </Item>
          </div>

          <form onSubmit={onSubmit} className="space-y-6">
            <FormField label={t("contact.form.name")}>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border-b border-border bg-transparent py-3 focus:border-primary focus:outline-none"
              />
            </FormField>
            <FormField label={t("contact.form.email")}>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-b border-border bg-transparent py-3 focus:border-primary focus:outline-none"
              />
            </FormField>
            <FormField label={t("contact.form.message")}>
              <textarea
                required
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full resize-none border-b border-border bg-transparent py-3 focus:border-primary focus:outline-none"
              />
            </FormField>
            <Button
              type="submit"
              size="lg"
              className="h-12 rounded-none bg-foreground px-10 t-caption uppercase tracking-[var(--tracking-wider)] text-background hover:bg-primary"
            >
              {t("contact.form.open_email")}
            </Button>
            <p className="text-sm text-muted-foreground">{t("contact.form.email_hint")}</p>
          </form>
        </div>
      </section>
    </SiteShell>
  );
}

function Item({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <p className="t-eyebrow tracking-[var(--tracking-wider)]">{title}</p>
      </div>
      <div className="mt-2 font-serif text-xl leading-tight text-foreground">{children}</div>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-2 block t-eyebrow-muted">
        {label}
      </label>
      {children}
    </div>
  );
}
